import type { SessionType } from "@/types"

export const queryKeys = {
  trainingModules: ["training-modules"] as const,
  trainingProgress: ["training-progress"] as const,
  uploadUrl: ["upload-url"] as const,
  uploadProgress: ["upload-progress"] as const,
  uploadVideo: ["upload-video"] as const,
  deleteChatSessions: ["delete-chat-sessions"] as const,
  deleteChatSession: ["delete-chat-session"] as const,
  renameChatSession: ["rename-chat-session"] as const,
  updateSessionProject: ["update-session-project"] as const,
  fullSession: (sessionId: string) => ["full-session", sessionId] as const,
  sessionHistory: (sessionType: SessionType, projectId?: string | null) =>
    projectId
      ? (["session-history", sessionType, projectId] as const)
      : (["session-history", sessionType] as const),
  runParser: (sessionId: string) => ["run-parser", sessionId] as const,
  parserTemplates: () => ["parser-templates"] as const,
  parserOptionTypes: () => ["parser-option-types"] as const,
  prompts: ["prompts"] as const,
  upsertPrompt: ["upsert-prompt"] as const,
  deletePrompt: ["delete-prompt"] as const,
  flowPrompts: ["flow-prompts"] as const,
  upsertFlowPrompt: ["upsert-flow-prompt"] as const,
  deleteFlowPrompt: ["delete-flow-prompt"] as const,
  trustedSources: ["trusted-sources"] as const,
  upsertTrustedSource: ["upsert-trusted-source"] as const,
  deleteTrustedSource: ["delete-trusted-source"] as const,
  workflows: ["workflows"] as const,
  sharedWorkflows: ["shared-workflows"] as const,
  workflow: (id: string) => ["workflow", id] as const,
  upsertWorkflow: ["upsert-workflow"] as const,
  updateWorkflowChat: (workflowId: string) => ["update-workflow-chat", workflowId] as const,
  deleteWorkflow: ["delete-workflow"] as const,
  toggleWorkflowFavorite: ["toggle-workflow-favorite"] as const,
  indexedFiles: (sessionId: string) => ["indexed-files", sessionId] as const,
  removeIndexedFile: ["remove-indexed-file"] as const,
  userSettings: ["user-settings"] as const,
  // Projects (general projects, not Studio AI)
  projects: (sessionType?: string) =>
    sessionType ? (["projects", sessionType] as const) : (["projects"] as const),
  project: (id: string) => ["project", id] as const,
  projectFiles: (projectId: string) => ["project-files", projectId] as const,
  projectEmbeddingStatus: (projectId: string) => ["project-embedding-status", projectId] as const,
  createProject: ["create-project"] as const,
  updateProject: (projectId: string) => ["update-project", projectId] as const,
  deleteProject: (projectId: string) => ["delete-project", projectId] as const,
  uploadProjectFiles: (projectId: string) => ["upload-project-files", projectId] as const,
  deleteProjectFile: (projectId: string) => ["delete-project-file", projectId] as const,
  // File picker operations
  graphFiles: (source: string, parentId?: string) =>
    parentId ? (["graph-files", source, parentId] as const) : (["graph-files", source] as const),
  graphDriveChildren: (driveId: string, itemId: string) =>
    ["graph-drive-children", driveId, itemId] as const,
  sharePointSites: () => ["sharepoint-sites"] as const,
  sharePointLibraries: (siteId: string) => ["sharepoint-libraries", siteId] as const,
  // Citations and navigation
  citations: (sessionId: string) => ["citations", sessionId] as const,
  navigatorLibraries: (sessionType: string) => ["navigator-libraries", sessionType] as const,
  // Session messages and agent state
  sessionMessages: (sessionId: string) => ["session-messages", sessionId] as const,
  agentState: (sessionId: string) => ["agent-state", sessionId] as const,
  // Active query keys for one chat (#3524). `incarnation` is set only after the
  // chat was deleted in a store (see ChatSessionQueryScope) and goes last, after
  // the fact-check cache buster, so prefix invalidation of sessionMessages /
  // agentState still reaches every variant. Without it the keys are unchanged.
  sessionMessagesQuery: (sessionId: string, cacheBuster?: string, incarnation?: string) =>
    [
      "session-messages",
      sessionId,
      ...(cacheBuster ? [cacheBuster] : []),
      ...(incarnation ? [{ incarnation }] : []),
    ] as const,
  agentStateQuery: (sessionId: string, incarnation?: string) =>
    ["agent-state", sessionId, ...(incarnation ? [{ incarnation }] : [])] as const,
  // File content
  citationFile: (url: string) => ["citation-file", url] as const,
  fileContent: (url: string) => ["file-content", url] as const,
  // System prompts and What's New
  systemPrompt: (promptType: string) => ["system-prompt", promptType] as const,
  whatsNew: (locale: string, previewVersion?: string) =>
    previewVersion
      ? (["whats-new", locale, "preview", previewVersion] as const)
      : (["whats-new", locale] as const),
  adminWhatsNewVersions: ["admin-whats-new-versions"] as const,
  // Directory search (users/groups)
  directorySearch: (query: string, types?: string) =>
    types ? (["directory-search", query, types] as const) : (["directory-search", query] as const),
  // Project participants
  projectParticipants: (projectId: string) => ["project-participants", projectId] as const,
  // Documents
  documents: (projectId?: string) =>
    projectId ? (["documents", projectId] as const) : (["documents"] as const),
  document: (documentId: string) => ["document", documentId] as const,
  documentVersions: (documentId: string) => ["document-versions", documentId] as const,
  documentParticipants: (documentId: string) => ["document-participants", documentId] as const,
  // Session sharing
  sessionParticipants: (sessionId: string) => ["session-participants", sessionId] as const,
  sharedSessions: (sessionType?: string) =>
    sessionType ? (["shared-sessions", sessionType] as const) : (["shared-sessions"] as const),
  // Dashboards
  dashboards: ["dashboards"] as const,
  dashboard: (id: string) => ["dashboards", id] as const,
  dashboardParticipants: (id: string) => ["dashboard-participants", id] as const,
  // Config
  config: ["config"] as const,
  // Fair use — the current user's usage snapshot (see useMyUsage())
  fairUseMyUsage: ["fair-use", "my-usage"] as const,
  /**
   * Fair use, admin side. Every key starts with the `adminFairUse` prefix on
   * purpose: the mutations invalidate that prefix once and reach the overview,
   * the month summary and the config together, so a top-up can never leave the
   * KPI row claiming a request is still waiting.
   */
  adminFairUse: ["admin", "fair-use"] as const,
  adminFairUseOverview: (month: string, filter: string) =>
    ["admin", "fair-use", "overview", month, filter] as const,
  /** Users & licences: the directory page, keyed by month AND search term. */
  adminFairUsePeople: (month: string, search: string) =>
    ["admin", "fair-use", "people", month, search] as const,
  adminFairUseSummary: (month: string) => ["admin", "fair-use", "summary", month] as const,
  adminFairUseConfig: ["admin", "fair-use", "config"] as const,
  // Hosted agents (HERMES) — end-user roster + per-agent thread lists.
  // Single-agent lookups derive from the roster key via `select`
  // (use-hosted-agents.ts), so there is no per-agent key.
  hostedAgents: ["hosted-agents"] as const,
  hostedAgentSessions: (slug: string) => ["hosted-agents", slug, "sessions"] as const,
  // B4: per-agent artifact library (latest version per family) + one artifact's family.
  hostedAgentArtifacts: (slug: string) => ["hosted-agents", slug, "artifacts"] as const,
  hostedAgentArtifact: (slug: string, artifactId: string) =>
    ["hosted-agents", slug, "artifacts", artifactId] as const,
  // #3023: per-agent working state (active facts only in the user view).
  hostedAgentWorkingState: (slug: string) => ["hosted-agents", slug, "working-state"] as const,
  sora2: {
    projects: () => ["sora2", "projects"] as const,
    project: (id: string) => ["sora2", "project", id] as const,
    jobs: (mediaType?: string) =>
      mediaType ? (["sora2", "jobs", mediaType] as const) : (["sora2", "jobs"] as const),
    videos: () => ["sora2", "videos"] as const,
    video: (id: string) => ["sora2", "video", id] as const,
    videoStatus: (jobId: string) => ["sora2", "status", jobId] as const,
    templates: () => ["sora2", "templates"] as const,
    template: (id: string) => ["sora2", "template", id] as const,
    batchStatus: (batchId: string) => ["sora2", "batch", batchId] as const,
    styleTemplates: (params?: Record<string, string>) =>
      params
        ? (["sora2", "style-templates", params] as const)
        : (["sora2", "style-templates"] as const),
  },
}
